import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Activity,
  Apple,
  BookOpen,
  Bot,
  ChevronRight,
  Dumbbell,
  HeartPulse,
  Share2,
  Shield,
  Smartphone,
  Video,
} from 'lucide-react';
import './MobileAppEmulatorPage.css';

const MODULES = [
  { id: 'practice-film', label: 'Practice Film', subtitle: 'Frame-by-frame overlay review with D4RT depth tracking', icon: Smartphone, badge: 'D4RT' },
  { id: 'biometrics', label: 'Biometrics', subtitle: 'Readiness and recovery signals from wearable integrations', icon: HeartPulse, badge: 'Live' },
  { id: 'load-management', label: 'Load Management', subtitle: 'Session strain, fatigue control, and ACWR monitoring', icon: Activity, badge: 'Weekly' },
  { id: 'workout-generator', label: 'Workout Generator', subtitle: 'AI-adaptive sessions targeting strength and conditioning goals', icon: Dumbbell, badge: 'AI' },
  { id: 'nutrition-diary', label: 'Nutrition Diary', subtitle: 'Macro balance, fueling windows, and meal planning', icon: Apple, badge: 'Macro' },
  { id: 'ai-coach', label: 'AI Coach', subtitle: 'Instant coaching prompts and development recommendations', icon: Bot, badge: 'Chat' },
  { id: 'smart-journal', label: 'Smart Journal', subtitle: 'Training context, reflections, and progress tracking', icon: BookOpen, badge: 'Notes' },
  { id: 'create-share', label: 'Create & Share', subtitle: 'Build clips, highlight reels, and publish quickly', icon: Share2, badge: 'Media' },
  { id: 'strength-admin', label: 'Strength Admin', subtitle: 'Team visibility, approvals, and staff management tools', icon: Shield, badge: 'Staff' },
];

const normalizeSport = (pathname) => {
  const section = pathname.split('/')[1] || 'basketball';
  return ['basketball', 'baseball', 'golf', 'soccer'].includes(section) ? section : 'basketball';
};

const endpointForModule = (sport, moduleId) => {
  const base = `/${sport}`;
  if (moduleId === 'practice-film') {
    return sport === 'basketball' ? '/basketball/slammobile' : `${base}/practice-film`;
  }
  if (moduleId === 'biometrics') return `${base}/biometrics`;
  if (moduleId === 'load-management') return `${base}/load-management`;
  if (moduleId === 'workout-generator') return `${base}/workout-generator`;
  if (moduleId === 'nutrition-diary') return `${base}/nutrition`;
  if (moduleId === 'ai-coach') return `${base}/ai-coach`;
  if (moduleId === 'smart-journal') return `${base}/journal`;
  if (moduleId === 'create-share') return `${base}/publish`;
  if (moduleId === 'strength-admin') return `${base}/strength-admin`;
  return sport === 'basketball' ? '/basketball/slammobile' : `${base}/practice-film`;
};

export default function MobileAppEmulatorPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const sport = useMemo(() => normalizeSport(location.pathname), [location.pathname]);
  const sportLabel = `${sport.charAt(0).toUpperCase()}${sport.slice(1)}`;

  const goToModule = (moduleId) => {
    navigate(endpointForModule(sport, moduleId));
  };

  return (
    <div className="mobile-hub-page">
      <header className="mobile-hub-header">
        <div className="mobile-hub-header-text">
          <p className="mobile-eyebrow">APP GLOSSARY</p>
          <h1 className="mobile-hub-title">{sportLabel} Mobile</h1>
          <p className="mobile-hub-subtitle">
            Each module provides specialized tools for athlete development and team management. Tap to open.
          </p>
        </div>
      </header>

      <div className="mobile-hub-grid">
        {MODULES.map((module) => {
          const Icon = module.icon;
          return (
            <button
              key={module.id}
              type="button"
              className="mobile-hub-card"
              onClick={() => goToModule(module.id)}
            >
              <div className="mobile-hub-card-icon">
                <Icon size={22} />
              </div>
              <div className="mobile-hub-card-body">
                <div className="mobile-hub-card-top">
                  <p className="mobile-hub-card-title">{module.label}</p>
                  <span className="mobile-hub-card-badge">{module.badge}</span>
                </div>
                <p className="mobile-hub-card-subtitle">{module.subtitle}</p>
              </div>
              <ChevronRight size={16} className="mobile-hub-card-arrow" />
            </button>
          );
        })}
      </div>
    </div>
  );
}
